import { useCallback, useMemo, useState } from "react"
import { useObjectsCategories } from "hooks/api/objectsCategories/objectsCategories.hook"
import {
	MIXED_LABEL,
	buildBatchCategoryConfirmText,
	buildBatchSelectionTitle,
	canResetBatchDeliveryDate,
	isCategoryUnified,
	isFieldDirty,
	isMixed,
	resolveBatchReviewToggle,
	resolveCustomizationEdits,
	resolveSingleObjectCategoryId,
} from "helpers/batchFurniture.helper"
import { ConfirmModal } from "components/ui/modals/ConfirmModal/ConfirmModal"
import { SegmentedWithoutBg } from "components/ui/segmented/SegmentedWithoutBg/SegmentedWithoutBg"
import { FurnitureHeader } from "../FurnitureSidebar/layout/FurnitureHeader/FurnitureHeader"
import { FurnitureImage } from "../FurnitureSidebar/layout/FurnitureImage/FurnitureImage"
import { ElevationReferenceSelect } from "../FurnitureSidebar/layout/ElevationReferenceSelect/ElevationReferenceSelect"
import { DeliveryDateSection } from "../FurnitureSidebar/layout/DeliveryDateSection/DeliveryDateSection"
import { ModelNumberSection } from "../FurnitureSidebar/layout/ModelNumberSection/ModelNumberSection"
import { DimensionsSection } from "../FurnitureSidebar/layout/DimensionsSection/DimensionsSection"
import { NameSection } from "../FurnitureSidebar/layout/NameSection/NameSection"
import { PriceSection } from "../FurnitureSidebar/layout/PriceSection/PriceSection"
import { AttachmentsSection } from "../FurnitureSidebar/layout/AttachmentsSection/AttachmentsSection"
import { NotesSection } from "../FurnitureSidebar/layout/NotesSection/NotesSection"
import { CategoriesSection } from "../FurnitureSidebar/layout/CategoriesSection/CategoriesSection"
import { CustomizationSection } from "../shared/CustomizationSection/CustomizationSection"
import { FurnitureFooter } from "../FurnitureSidebar/layout/FurnitureFooter/FurnitureFooter"
import { ConnectedCalloutsTab } from "../FurnitureSidebar/layout/ConnectedCalloutsTab/ConnectedCalloutsTab"
import { CUSTOM_TOP_LEVEL_CATEGORY, OTHER_TOP_LEVEL_CATEGORY } from "../FurnitureSidebar/furniture.constants"
import { getEcallsForErefId, isFurnitureQuotationReady } from "../quotationViewer.helpers"
import { useBatchFurnitureForm } from "./useBatchFurnitureForm.hook"
import type { ICategoriesData } from "types/configuration.type"
import type {
	TBatchConfigPatch,
	TBatchCountertopAttachmentsPatch,
	TEcall,
	TEref,
	TFurniture,
	TFurnitureCustomization,
	TFurnitureStatus,
	TPageElevationData,
	TRelatedCategoryValue,
	TSelectedModelNumber,
} from "types/elevationReferences.type"
import type { TQuotationDetail } from "types/quotations.type"
import type { TCustomModelNumber } from "types/customModelNumbers.type"
// The frame is the single-object sidebar's — same column layout, same dividers, same tab strip —
// because the two panels are the same surface with a different subject. Imported rather than
// copied so there is ONE set of measurements: a duplicate is what would let them drift apart.
// (ReferenceSidebar / EcallSidebar keep their own frames; theirs genuinely differ.)
import style from "../FurnitureSidebar/FurnitureSidebar.module.scss"

const { sidebar, body, divider, content, segmentedWrapper, segmented } = style

type TabOption = "Details" | "Connected callouts"
const TAB_OPTIONS: TabOption[] = ["Details", "Connected callouts"]

const EMPTY_RELATED_OBJECTS: TPageElevationData[] = []
const EMPTY_ECALLS: TEcall[] = []
const EMPTY_DELIVERY_DATES: NonNullable<TQuotationDetail["configuration"]>["delivery_dates"] = []

type Props = {
	/** The live selection, in selection order. Two or more — the panel is meaningless below that. */
	furniture: TFurniture[]
	relatedObjects?: TPageElevationData[]
	/** Every callout in the quotation; the panel narrows them to the elevation the selection shares. */
	ecalls?: TEcall[]
	/** The quotation's configured delivery dates (#1390), same list the single-object sidebar reads. */
	deliveryDates?: NonNullable<TQuotationDetail["configuration"]>["delivery_dates"]
	isPending?: boolean
	/** Session-wide "Don't show this again" flag for the category-change confirmation (§4.5). */
	skipCategoryConfirm: boolean
	onSkipCategoryConfirmChange: (value: boolean) => void
	onClose?: () => void
	/**
	 * Opens the elevation the whole selection shares. The back chevron only appears when there IS
	 * one — with the selection spread over several elevations there is no single place to go back to.
	 */
	onNavigateToEref?: (erefId: number) => void
	/** Raises the shared batch delete confirmation. Omit to hide the ⋯ menu. */
	onDelete?: () => void
	onView?: (ecall: TEcall) => void
	onErefChange: (erefId: number | null) => void
	/**
	 * Every commit resolves to whether the backend APPLIED it. `false` (a 400, or a clear the endpoint
	 * has no null for) means the objects are untouched, and the panel puts the displayed value back —
	 * see `revertOnRejection`.
	 */
	onCommitDimension: (key: "width" | "height" | "depth", value: number | null) => Promise<boolean>
	/** `null` clears the override (the selection goes back to inheriting its elevations' dates). */
	onCommitDeliveryDate: (deliveryDateId: number | null) => Promise<boolean>
	onCommitModelNumber: (
		model: TSelectedModelNumber,
		options: { useArbitraryEndpoint: boolean; customModelNumber?: TCustomModelNumber | null },
	) => Promise<boolean>
	onCommitConfig: (patch: TBatchConfigPatch) => Promise<boolean>
	/** REQ-449. */
	onCommitCountertopAttachments: (patch: TBatchCountertopAttachmentsPatch) => Promise<boolean>
	onCommitNotes: (notes: string) => Promise<boolean>
	onCommitName: (name: string) => Promise<boolean>
	onCommitPrice: (price: number | null) => Promise<boolean>
	/** `null` moves the whole selection to Other / fixed-price mode, `"custom"` to custom objects. */
	onCommitCategory: (relatedCategoryId: TRelatedCategoryValue) => Promise<boolean>
	/**
	 * REQ-431. Only the field(s) actually edited — `resolveCustomizationEdits`'s job. The field the
	 * caller omits is preserved per-object rather than reset (the endpoint requires both, so the
	 * hook groups the selection by the omitted field's real value and sends one request per group).
	 */
	onCommitCustomization: (edits: Partial<TFurnitureCustomization>) => Promise<boolean>
	/** REQ-431. Clears the customization from every selected object. */
	onRemoveCustomization: () => Promise<boolean>
	onSetStatus: (status: TFurnitureStatus) => void
}

/**
 * Right-panel editor for a multi-furniture selection (REQ-403).
 *
 * Every section here is the single-object sidebar's own, extended for the `Mixed` state rather than
 * forked: a field the selection agrees on behaves exactly as it does for one object, and one it
 * disagrees on renders empty with a `Mixed` placeholder. Typing into such a field applies that one
 * value to every selected object; leaving it alone omits it from the request, which is how the
 * objects keep their differing values.
 *
 * Which sections exist is the INTERSECTION of the selection's property sets — the model number and
 * attachments only for an all-cabinet selection, name + price only for an all-Other one — mirroring
 * what the batch endpoints accept (`configuration/` rejects countertops and Other, `fixed-price/`
 * accepts only Other).
 *
 * The `Customization` section (REQ-431) reuses the single-object `CustomizationSection` verbatim,
 * extended with two placeholder props for the Mixed case. Gated PER FIELD, like the category
 * levels below: note and price are each compared independently, so a selection that shares one but
 * disagrees on the other shows the real shared value in the one and `Mixed` only in the other
 * (review feedback on #1388 — showing BOTH as Mixed just because they differ hides real, shared
 * information the panel could otherwise surface). Whether the section is expanded at all is the
 * separate `hasCustomization` question: a selection split between "has one" and "has none" always
 * expands, even in the rare case its note/price happen to coincide; only a selection that
 * unanimously has none starts collapsed. Opening it with `+` writes `{ note: "", price_adjustment:
 * null }` to every selected object, REPLACING whatever each one held, exactly as the ticket
 * specifies; typing into a Mixed field does the same with the typed values.
 *
 * The batch endpoint (`PUT .../furniture/customization/`) requires BOTH `note` and
 * `price_adjustment` together — unlike the eref configuration endpoint, it has no optional fields
 * to omit an untouched one. `resolveCustomizationEdits` is what still makes committing each field
 * independently on its own blur safe: it reports only the field that actually changed, and
 * `useBatchFurnitureEdit.commitCustomization` preserves the OTHER field's real per-object value by
 * grouping the selection on it (one request per group) instead of forcing it to the edited field's
 * Mixed baseline. See that hook's doc comment for the grouping itself.
 */
export const BatchFurnitureSidebar = ({
	furniture,
	relatedObjects = EMPTY_RELATED_OBJECTS,
	ecalls = EMPTY_ECALLS,
	deliveryDates = EMPTY_DELIVERY_DATES,
	isPending = false,
	skipCategoryConfirm,
	onSkipCategoryConfirmChange,
	onClose,
	onNavigateToEref,
	onDelete,
	onView,
	onErefChange,
	onCommitDimension,
	onCommitDeliveryDate,
	onCommitModelNumber,
	onCommitConfig,
	onCommitCountertopAttachments,
	onCommitNotes,
	onCommitName,
	onCommitPrice,
	onCommitCategory,
	onCommitCustomization,
	onRemoveCustomization,
	onSetStatus,
}: Props) => {
	const form = useBatchFurnitureForm(furniture)
	const [tab, setTab] = useState<TabOption>("Details")
	const { data: objectsCategoriesData } = useObjectsCategories()

	/**
	 * The category change waiting on the confirmation modal, `null` when none is. Held here rather
	 * than sent straight away because the modal must be able to leave the selection untouched.
	 */
	const [pendingCategory, setPendingCategory] = useState<{ id: TRelatedCategoryValue; name: string } | null>(null)
	const [dontAskAgain, setDontAskAgain] = useState(false)

	const { fields, sections, categorySelection, resyncFromShared } = form
	const count = furniture.length

	/**
	 * Restores every displayed value once a commit reports it was NOT applied.
	 *
	 * A rejection leaves the objects exactly as they were, so the derived shared values are unchanged
	 * and the form's own resync effects can never fire — without this, a typed dimension, an
	 * optimistically-lit segment or an already-moved category select would keep showing a value the
	 * backend refused.
	 */
	const revertOnRejection = useCallback(
		(result: Promise<boolean> | void) => {
			// Only an explicit `false` is a rejection. A handler that reports nothing back has expressed
			// no verdict, and treating that silence as failure would revert a field the backend did
			// accept — hence `Promise.resolve`, which also makes a handler returning nothing safe.
			void Promise.resolve(result).then((applied) => {
				if (applied === false) resyncFromShared()
			})
		},
		[resyncFromShared],
	)

	// Each field commits independently on its own blur, exactly like every sibling field in this
	// panel (Dimensions' width/height/depth included) — `resolveCustomizationEdits` is what makes
	// that safe here despite the PUT requiring both `note` and `price_adjustment` on every request:
	// it reports only the field that actually changed, and `useBatchFurnitureEdit.commitCustomization`
	// preserves the OTHER field's real per-object value by grouping the selection on it, rather than
	// forcing it to this field's Mixed baseline. See that hook's doc comment for the grouping itself.
	const commitCustomizationFields = useCallback(() => {
		const edits = resolveCustomizationEdits(
			fields.customizationNote,
			fields.customizationPriceAdjustment,
			form.customizationNote,
			form.customizationPriceAdjustment,
		)
		if (Object.keys(edits).length === 0) return
		revertOnRejection(onCommitCustomization(edits))
	}, [
		fields.customizationNote,
		fields.customizationPriceAdjustment,
		form.customizationNote,
		form.customizationPriceAdjustment,
		onCommitCustomization,
		revertOnRejection,
	])

	// Only erefs on the selection's own page are valid targets. Reading page 0's own is safe because
	// the panel never opens on a cross-page selection in the first place — `useBatchFurnitureEdit`'s
	// selection sync refuses one (every batch endpoint 400s on it), so all `furniture` share a page.
	const pageNumber = furniture[0]?.page_number
	const pageErefs = useMemo<TEref[]>(
		() => relatedObjects.find((page) => page.page_number === pageNumber)?.erefs ?? [],
		[relatedObjects, pageNumber],
	)

	/**
	 * Sends a category change, raising the confirmation first when the selection does not already
	 * agree on one category (§4.5) and the user hasn't opted out for the session. A selection that
	 * already shares a category is just an ordinary edit — nothing is being unified, so there is
	 * nothing to warn about.
	 */
	const requestCategoryChange = useCallback(
		(id: TRelatedCategoryValue, name: string) => {
			if (skipCategoryConfirm || isCategoryUnified(furniture)) {
				revertOnRejection(onCommitCategory(id))
				return
			}
			setDontAskAgain(false)
			setPendingCategory({ id, name })
		},
		[skipCategoryConfirm, furniture, onCommitCategory, revertOnRejection],
	)

	/**
	 * Picking a top level. "Other" is immediately actionable — it maps to `related_category_id: null`.
	 * Any other top level needs a LEAF category id, which only exists up front when that branch of
	 * `/categories/` holds exactly one (Countertops, the case the design flow shows); otherwise this
	 * only moves local state and the commit happens when the Object level is picked.
	 */
	const handleTopLevelChange = useCallback(
		(value: string | number | null) => {
			categorySelection.handleTopLevelChange(value)
			if (value === OTHER_TOP_LEVEL_CATEGORY) {
				requestCategoryChange(null, OTHER_TOP_LEVEL_CATEGORY)
				return
			}
			// #1346 — immediately actionable like "Other": the endpoint takes the literal string, so
			// there is no leaf category to wait for.
			if (value === CUSTOM_TOP_LEVEL_CATEGORY) {
				requestCategoryChange("custom", CUSTOM_TOP_LEVEL_CATEGORY)
				return
			}
			if (typeof value !== "string") return
			const singleLeafId = resolveSingleObjectCategoryId(objectsCategoriesData as ICategoriesData | undefined, value)
			if (singleLeafId === null) return
			if (!isMixed(fields.objectCategoryId) && fields.objectCategoryId === singleLeafId) return
			requestCategoryChange(singleLeafId, value)
		},
		[categorySelection, requestCategoryChange, objectsCategoriesData, fields.objectCategoryId],
	)

	const handleObjectCategoryChange = useCallback(
		(value: string | number | null) => {
			categorySelection.handleModelCategoryChange(value)
			const nextId = typeof value === "number" ? value : value !== null ? Number(value) : null
			if (nextId === null || !Number.isFinite(nextId)) return
			if (!isMixed(fields.objectCategoryId) && fields.objectCategoryId === nextId) return
			const label = categorySelection.modelCategoryOptions.find((option) => option.value === nextId)?.label
			requestCategoryChange(nextId, typeof label === "string" ? label : "the selected category")
		},
		[categorySelection, requestCategoryChange, fields.objectCategoryId],
	)

	const confirmCategoryChange = useCallback(() => {
		if (!pendingCategory) return
		if (dontAskAgain) onSkipCategoryConfirmChange(true)
		// A rejected category change has to put the three selects back too: the pick already

		// moved them locally, and the refetch after a 400 produces identical defaults, so nothing

		// else would.

		revertOnRejection(onCommitCategory(pendingCategory.id))
		setPendingCategory(null)
	}, [pendingCategory, dontAskAgain, onSkipCategoryConfirmChange, onCommitCategory, revertOnRejection])

	// Cancelling must leave the selection untouched — including the selects, which the pick already
	// moved locally. `syncDefaults` puts them back on what the objects actually hold.
	const cancelCategoryChange = useCallback(() => {
		setPendingCategory(null)
		categorySelection.syncDefaults()
	}, [categorySelection])

	const confirmText = pendingCategory ? buildBatchCategoryConfirmText(count, pendingCategory.name) : null

	// The whole selection has to sit on ONE elevation for "back" to mean anything.
	const sharedErefId = isMixed(fields.erefId) ? null : fields.erefId
	const connectedEcalls = useMemo(() => getEcallsForErefId(ecalls, sharedErefId), [ecalls, sharedErefId])

	/**
	 * Whether "Inherit from elevation" is offered for the delivery date (#1390). Deliberately NOT
	 * gated on the selection's CURRENTLY DISPLAYED date being non-Mixed — the reset ("clear every
	 * selected object's override") is one well-defined action regardless of what today's values show.
	 * `canResetBatchDeliveryDate` owns the real conditions: every object needs an eref, those
	 * elevations must resolve to the same date, and at least one object needs an actual override —
	 * otherwise the button would be offering a reset that changes nothing.
	 */
	const canResetDeliveryDate = canResetBatchDeliveryDate(furniture, pageErefs)

	/**
	 * The series the model-number picker prices against: the shared elevation's override, when the
	 * whole selection sits on one elevation. A selection spread over several has no single series, so
	 * the picker falls back to its own "No series selected" warning — which is the truth, not a bug.
	 */
	const seriesOverrideId = useMemo<number | null>(() => {
		if (sharedErefId === null) return null
		const eref = pageErefs.find((candidate) => candidate.id === sharedErefId)
		return eref?.estimation_config?.series_override_id ?? null
	}, [pageErefs, sharedErefId])

	/**
	 * The review toggle, decided by REQ-369's shared rule rather than by `form.status`. A mixed-status
	 * selection reports `null` status, which as a plain boolean read as "not reviewed" and rendered
	 * "Mark as reviewed" — firing it would have sent `reviewed` to every object, silently pulling any
	 * `in_quotation` one back out of the quotation. A single object's sidebar never offers that, and
	 * REQ-403 requires the batch panel to behave as the per-object edit does.
	 */
	const reviewToggle = useMemo(() => resolveBatchReviewToggle(furniture), [furniture])
	const isReviewed = reviewToggle?.key === "unreview"
	const isInQuotation = form.status === "in_quotation"
	// Every selected object must be complete: the endpoint applies the status to all of them, so one
	// unfinished object is enough to reject the whole batch.
	const canAddToQuotation = furniture.every(isFurnitureQuotationReady)

	return (
		<div className={sidebar} data-hotkey-sidebar tabIndex={-1}>
			<FurnitureHeader
				title={buildBatchSelectionTitle(count)}
				showStatusIcon={false}
				onClose={onClose}
				onBack={
					onNavigateToEref && sharedErefId !== null ? () => onNavigateToEref(sharedErefId) : undefined
				}
				onDelete={onDelete}
			/>
			<div className={content}>
				<div className={segmentedWrapper}>
					<SegmentedWithoutBg
						options={TAB_OPTIONS}
						value={tab}
						onChange={(value) => setTab(value as TabOption)}
						size="middle"
						className={segmented}
					/>
				</div>
				{tab === "Connected callouts" ? (
					<ConnectedCalloutsTab
						ecalls={connectedEcalls}
						erefId={form.selectedErefId}
						onView={(ecall) => onView?.(ecall)}
					/>
				) : (
					<div className={body}>
						<FurnitureImage
							imageUrl={form.imageUrl}
							isCountertop={form.kind === "countertop"}
							hasModelNumber={form.sharedModelNumber !== null}
							isOther={form.kind === "other"}
							isCustom={form.kind === "custom"}
							message={form.previewMessage}
						/>

						<ElevationReferenceSelect
							erefs={pageErefs}
							value={form.selectedErefId}
							onChange={onErefChange}
							placeholder={isMixed(fields.erefId) ? MIXED_LABEL : undefined}
						/>

						<DeliveryDateSection
							deliveryDates={deliveryDates}
							value={form.sharedDeliveryDate}
							hasEref={furniture.every((f) => f.eref_id != null)}
							canReset={canResetDeliveryDate}
							placeholder={isMixed(fields.deliveryDate) ? MIXED_LABEL : undefined}
							onChange={onCommitDeliveryDate}
							onReset={() => onCommitDeliveryDate(null)}
						/>

						<div className={divider} />

						{sections.modelNumber ? (
							<>
								<ModelNumberSection
									furniture={form.modelNumberTarget}
									selectedModelNumber={form.sharedModelNumber}
									seriesOverrideId={seriesOverrideId}
									// The per-series price warning needs the picked model's pricing detail, which is
									// only fetched for a single object. The picker's own `No price` tag already flags
									// a row with no price for the series, so nothing is hidden from the user here.
									missingPriceSeriesName={null}
									// #1346 — an all-custom selection disagreeing on its definition is `Mixed` for the
									// same reason a cabinet selection is: the chip has to report the disagreement rather
									// than pick one object's value to show.
									mixedLabel={
										isMixed(form.isCustomSelection ? fields.customModelNumber : fields.modelNumber)
											? MIXED_LABEL
											: undefined
									}
									isCustom={form.isCustomSelection}
									customModelNumber={form.sharedCustomModelNumber}
									dimensionGroups={form.dimensionGroups}
									onSelect={async (model, options) => {
										// A rejected pick needs no revert: the chip renders the DERIVED shared model number,
										// not local state, so it already shows what the objects still hold.
										await onCommitModelNumber(model, {
											useArbitraryEndpoint: options.useArbitraryEndpoint,
											customModelNumber: options.customModelNumber,
										})
									}}
								/>
								<div className={divider} />
							</>
						) : null}

						<DimensionsSection
							width={form.width}
							height={form.height}
							depth={form.depth}
							onWidthChange={form.setWidth}
							onHeightChange={form.setHeight}
							onDepthChange={form.setDepth}
							onWidthCommit={() => {
								if (!isFieldDirty(fields.width, form.width, null)) return
								revertOnRejection(onCommitDimension("width", form.width))
							}}
							onHeightCommit={() => {
								if (!isFieldDirty(fields.height, form.height, null)) return
								revertOnRejection(onCommitDimension("height", form.height))
							}}
							onDepthCommit={() => {
								if (!isFieldDirty(fields.depth, form.depth, null)) return
								revertOnRejection(onCommitDimension("depth", form.depth))
							}}
							isRequired={form.kind !== "other"}
							showLnFt={form.showLnFt}
							placeholders={{
								width: form.mixedPlaceholder(isMixed(fields.width)),
								height: form.mixedPlaceholder(isMixed(fields.height)),
								depth: form.mixedPlaceholder(isMixed(fields.depth)),
							}}
						/>

						{sections.nameAndPrice ? (
							<>
								<div className={divider} />
								<NameSection
									value={form.name}
									onChange={form.setName}
									onBlur={() => {
										if (!isFieldDirty(fields.name, form.name, "")) return
										revertOnRejection(onCommitName(form.name))
									}}
									placeholder={form.mixedPlaceholder(isMixed(fields.name))}
								/>
								<PriceSection
									value={form.price}
									onChange={form.setPrice}
									onBlur={() => {
										if (!isFieldDirty(fields.price, form.price, null)) return
										revertOnRejection(onCommitPrice(form.price))
									}}
									placeholder={form.mixedPlaceholder(isMixed(fields.price))}
								/>
							</>
						) : null}

						{sections.attachments ? (
							<>
								<div className={divider} />
								<AttachmentsSection
									variant="cabinet"
									adjustableShelves={form.adjustableShelves}
									onAdjustableShelvesChange={form.setAdjustableShelves}
									onAdjustableShelvesCommit={() => {
										if (!isFieldDirty(fields.adjustedShelves, form.adjustableShelves, null)) return
										// `adjusted_shelves` is a non-nullable integer 0-10 on the endpoint, so a batch cannot
										// unset it. Put the stored value back rather than leaving the field blank over a number
										// the backend still holds.
										if (form.adjustableShelves === null) {
											resyncFromShared()
											return
										}
										revertOnRejection(onCommitConfig({ adjusted_shelves: form.adjustableShelves }))
									}}
									adjustableShelvesPlaceholder={form.mixedPlaceholder(isMixed(fields.adjustedShelves))}
									hasLocks={form.hasLocks}
									onHasLocksChange={(value) => {
										form.setHasLocks(value)
										revertOnRejection(onCommitConfig({ has_locks: value === "yes" }))
									}}
									hasFinishedBacks={form.hasFinishedBacks}
									onHasFinishedBacksChange={(value) => {
										form.setHasFinishedBacks(value)
										revertOnRejection(onCommitConfig({ has_finished_backs: value === "yes" }))
									}}
									fillings={form.fillings}
									onFillingsChange={(value) => {
										form.setFillings(value)
										revertOnRejection(onCommitConfig({ number_of_fillers: value as 0 | 1 | 2 }))
									}}
									finishingEnds={form.finishingEnds}
									onFinishingEndsChange={(value) => {
										form.setFinishingEnds(value)
										revertOnRejection(onCommitConfig({ number_of_finishing_ends: value as 0 | 1 | 2 }))
									}}
								/>
							</>
						) : null}

						{sections.countertopAttachments ? (
							<>
								<div className={divider} />
								<AttachmentsSection
									variant="countertop"
									grommets={form.countertopGrommets}
									onGrommetsChange={form.setCountertopGrommets}
									onGrommetsCommit={() => {
										if (!isFieldDirty(fields.countertopGrommets, form.countertopGrommets, null)) return
										// Non-nullable integer on the endpoint (auto-scan-backend#2181/#2183) — a batch
										// cannot unset it, same rule `adjustableShelves` follows above.
										if (form.countertopGrommets === null) {
											resyncFromShared()
											return
										}
										revertOnRejection(onCommitCountertopAttachments({ grommets: form.countertopGrommets }))
									}}
									wallCleats={form.countertopWallCleats}
									onWallCleatsChange={form.setCountertopWallCleats}
									onWallCleatsCommit={() => {
										if (!isFieldDirty(fields.countertopWallCleats, form.countertopWallCleats, null)) return
										if (form.countertopWallCleats === null) {
											resyncFromShared()
											return
										}
										revertOnRejection(onCommitCountertopAttachments({ wall_cleats: form.countertopWallCleats }))
									}}
									sideSplashes={form.countertopSideSplashes}
									onSideSplashesChange={(value) => {
										form.setCountertopSideSplashes(value)
										revertOnRejection(onCommitCountertopAttachments({ side_splash: value }))
									}}
									countertopAttachmentsPlaceholder={form.mixedPlaceholder(
										isMixed(fields.countertopGrommets) || isMixed(fields.countertopWallCleats),
									)}
								/>
							</>
						) : null}

						<div className={divider} />
						<CustomizationSection
							isExpanded={form.isCustomizationExpanded}
							note={form.customizationNote}
							priceAdjustment={form.customizationPriceAdjustment}
							onExpand={() => {
								form.expandCustomization()
								revertOnRejection(onCommitCustomization({ note: "", price_adjustment: null }))
							}}
							onRemove={() => {
								form.collapseCustomization()
								revertOnRejection(onRemoveCustomization())
							}}
							onNoteChange={form.setCustomizationNote}
							onPriceAdjustmentChange={form.setCustomizationPriceAdjustment}
							notePlaceholder={form.isCustomizationNoteMixed ? MIXED_LABEL : undefined}
							priceAdjustmentPlaceholder={form.isCustomizationPriceAdjustmentMixed ? MIXED_LABEL : undefined}
							onCommit={commitCustomizationFields}
						/>

						<div className={divider} />
						<NotesSection
							value={form.notes}
							onChange={form.setNotes}
							onBlur={() => {
								if (!isFieldDirty(fields.notes, form.notes, "")) return
								revertOnRejection(onCommitNotes(form.notes))
							}}
							placeholder={form.mixedPlaceholder(isMixed(fields.notes))}
						/>

						<div className={divider} />

						<CategoriesSection
							topLevelValue={categorySelection.selectedTopLevelCategory}
							topLevelOptions={categorySelection.topLevelOptions}
							onTopLevelChange={handleTopLevelChange}
							superValue={categorySelection.selectedSuperCategory}
							superOptions={categorySelection.superCategoryOptions}
							onSuperChange={categorySelection.handleSuperCategoryChange}
							modelCategoryValue={categorySelection.selectedModelCategory}
							modelCategoryOptions={categorySelection.modelCategoryOptions}
							onModelCategoryChange={handleObjectCategoryChange}
							// Gated per level, like every other field in the panel: only a level the selection
							// genuinely disagrees on reads Mixed. Two objects that both stop at the top level
							// AGREE about having no super category (`sharedValue` returns null, not MIXED).
							mixedLabels={{
								topLevel: form.mixedPlaceholder(isMixed(fields.topLevelCategory)),
								super: form.mixedPlaceholder(isMixed(fields.superCategory)),
								object: form.mixedPlaceholder(isMixed(fields.objectCategoryId)),
							}}
							showAllLevels={sections.lowerCategories}
						/>
					</div>
				)}
			</div>
			{tab === "Details" ? (
				<FurnitureFooter
					isReviewed={isReviewed}
					isInQuotation={isInQuotation}
					isPending={isPending}
					showReviewToggle={reviewToggle !== null}
					onToggleReviewed={() => reviewToggle && onSetStatus(reviewToggle.targetStatus)}
					canAddToQuotation={canAddToQuotation}
					onAddToQuotation={() => onSetStatus("in_quotation")}
					onRemoveFromQuotation={() => onSetStatus("reviewed")}
				/>
			) : null}
			{confirmText ? (
				<ConfirmModal
					isOpen
					onClose={cancelCategoryChange}
					onConfirm={confirmCategoryChange}
					title={confirmText.title}
					description={confirmText.description}
					confirmLabel="Change"
					cancelLabel="Cancel"
					isPending={isPending}
					dontShowAgain={{ checked: dontAskAgain, onChange: setDontAskAgain }}
				/>
			) : null}
		</div>
	)
}
